import { tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { env } from '../environments/environment';
import { BuyResponseDto, Currency } from './api';
import { InventoryItemDto } from './api/inventoryItemDto';
import { NarwhalQueryParameters } from './api/narwhal.query';
import { defs } from './defs';
import { OfferDto } from './api/offerDto';
import { NarwhalListResultDto, NarwhalDto } from './api/narwhalListResultDto';

@Injectable()
export class NarwhalService {
	inventoryItems$: BehaviorSubject<InventoryItemDto[]>;
	populationQueryParams: BehaviorSubject<any>;
	mySwarmQueryParams: BehaviorSubject<any>;
	populationQueryParams$: Observable<any>;
	mySwarmQueryParams$: Observable<any>;
	totalCount$: Subject<number>;

	constructor(private http: HttpClient) {
		this.populationQueryParams = new BehaviorSubject({});
		this.populationQueryParams$ = this.populationQueryParams.asObservable();
		this.mySwarmQueryParams = new BehaviorSubject({});
		this.mySwarmQueryParams$ = this.mySwarmQueryParams.asObservable();
		this.totalCount$ = new Subject();
		this.inventoryItems$ = new BehaviorSubject([]);
	}

	setPopulationQueryParams(queryParams) {
		this.populationQueryParams.next(queryParams);
		localStorage.setItem(defs.populationFilter, JSON.stringify(queryParams));
	}

	setMySwarmQueryParams(queryParams) {
		this.mySwarmQueryParams.next(queryParams);
		localStorage.setItem(defs.mySwarmFilter, JSON.stringify(queryParams));
	}

	getCurrentUserId(): string {
		return localStorage.getItem(defs.userId);
	}

	/**
	 * Get the narwhal population
	 */
	getNarwhals(queryParams: NarwhalQueryParameters, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
		let params = new HttpParams();

		for (const key in queryParams) {
			if (queryParams.hasOwnProperty(key)) {
				params = params.set(key, queryParams[key]);
			}
		}
		if (this.getCurrentUserId() === queryParams.owner) {
			params = params.set('so', defs.sortOrders.mySwarm[queryParams.sortBy]);
		} else {
			params = params.set('so', defs.sortOrders.population[queryParams.sortBy]);
		}
		return this.http
			.get<NarwhalListResultDto>(`${env.beURL}/narwhal/list`, {
				params,
				observe,
				reportProgress
			})
			.pipe(
				tap(result => {
					this.totalCount$.next(result.total);
					result.inventoryItems && this.inventoryItems$.next(result.inventoryItems);
					result.items.forEach(n => NarwhalDto.mixin(n))
				})
			);
	}

	buyNarwhal = (narwhalId: string, currency: Currency): Promise<BuyResponseDto> => {
		const body = JSON.stringify({narwhalId, currency});
		return this.http.post<BuyResponseDto>(`${env.beURL}/narwhal/buy`, body).toPromise();
	};

	applyImprovements(narwhalId: string, catPartIds: string[]): Observable<any> {
		const body = JSON.stringify({narwhalId, catPartIds});
		return this.http.post(`${env.beURL}/narwhal/apply`, body);
	}

	stopSell(narwhalId: string): Observable<any> {
		return this.http.delete(`${env.beURL}/narwhal/stop-sell/${narwhalId}`);
	}

	like(narwhalId: string): Observable<any> {
		return this.http.get(env.beURL + '/narwhal/like/' + narwhalId);
	}

	offerNarwhal(offer: OfferDto, narwhalId: string): Promise<OfferDto> {
		const body = JSON.stringify(offer);
		return this.http.post(env.beURL + '/narwhal/sell/' + narwhalId, body).toPromise().then(() => (offer));
	}

	rename(narwhalId: string, name: string): Observable<any> {
		const body = JSON.stringify({narwhalId, name});
		return this.http.post(`${env.beURL}/narwhal`, body);
	}
}